import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, FileText, Eye, Pencil, Copy, Trash2, Power, RefreshCw, Landmark, Loader2, ChevronRight, MapPin, X, MousePointer2 } from 'lucide-react';
import TemplateMappingDialog from '../components/TemplateMappingDialog';
import TemplatePositionEditor from '../components/TemplatePositionEditor';

const ORG_TYPES = ['cemetery', 'church', 'council', 'diocese', 'burial_authority', 'other'];
const FORMAT_LABEL = {
  builtin: 'Built-in', pdf_fillable: 'Fillable PDF', pdf_flat: 'PDF', pdf_scanned: 'Scanned PDF', docx: 'Word (DOCX)',
};
const STATUS_META = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  mapping: { label: 'Needs mapping', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  disabled: { label: 'Disabled', cls: 'bg-slate-100 text-slate-500 border-slate-300' },
};

export default function ApplicationForms() {
  const { token, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadOrgType, setUploadOrgType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [positionFor, setPositionFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/application-templates`, auth);
      setTemplates(r.data || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doUpload = async () => {
    if (!uploadFile) { toast.error('Choose a form file first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('name', uploadName);
      fd.append('organisation_type', uploadOrgType);
      const r = await axios.post(`${API}/application-templates/upload`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Form analysed — review the field mapping');
      setUploadOpen(false); setUploadFile(null); setUploadName(''); setUploadOrgType('');
      await load();
      setMapping(r.data);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Upload/analysis failed');
    } finally { setUploading(false); }
  };

  const openTemplate = async (id) => {
    try {
      const r = await axios.get(`${API}/application-templates/${id}`, auth);
      setMapping(r.data);
    } catch { toast.error('Could not open template'); }
  };

  const openPosition = async (id) => {
    try {
      const r = await axios.get(`${API}/application-templates/${id}`, auth);
      setPositionFor(r.data);
    } catch { toast.error('Could not open template'); }
  };

  const preview = async (id) => {
    try {
      const r = await axios.post(`${API}/application-templates/${id}/preview`, {}, { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };

  const toggleStatus = async (t) => {
    const action = t.status === 'disabled' ? 'activate' : 'disable';
    try {
      await axios.post(`${API}/application-templates/${t.id}/${action}`, {}, auth);
      toast.success(action === 'activate' ? 'Template activated' : 'Template disabled');
      load();
    } catch { toast.error('Failed'); }
  };

  const duplicate = async (id) => {
    try { await axios.post(`${API}/application-templates/${id}/duplicate`, {}, auth); toast.success('Template duplicated'); load(); }
    catch { toast.error('Failed to duplicate'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template permanently?')) return;
    try { await axios.delete(`${API}/application-templates/${id}`, auth); toast.success('Template deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const onReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/application-templates/${replaceId}/replace-source`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Source replaced — new version needs re-mapping');
      await load();
      setMapping(r.data);
    } catch (er) { toast.error(er.response?.data?.detail || 'Replace failed'); }
    finally { setReplaceId(null); if (replaceRef.current) replaceRef.current.value = ''; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <button onClick={() => navigate('/settings')} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-3" data-testid="back-to-settings">
          Dashboard <ChevronRight className="w-3 h-3" /> Settings <ChevronRight className="w-3 h-3" /> Application Forms
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FileText className="w-6 h-6 text-teal-600" /> Application Form Templates
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Turn any council, cemetery, diocese or church form into a reusable template. Your order data fills it automatically —
              you only complete what's missing, and the authority's original form is generated.
            </p>
          </div>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setUploadOpen(true)} data-testid="upload-form-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload Application Form
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="templates-grid">
            {templates.map((t) => {
              const isBuiltin = t.kind === 'builtin_oxford';
              const sm = STATUS_META[t.status] || STATUS_META.active;
              return (
                <Card key={t.id} className={`border-l-4 ${isBuiltin ? 'border-l-indigo-500' : 'border-l-teal-500'}`} data-testid={`template-card-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                          {isBuiltin && <Landmark className="w-4 h-4 text-indigo-500 shrink-0" />}
                          <span className="truncate">{t.name}</span>
                        </p>
                        <p className="text-xs text-slate-500 truncate">{t.organisation_name || '—'}</p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <Badge className={`border text-[10px] ${sm.cls}`}>{sm.label}</Badge>
                          <Badge variant="outline" className="text-[10px]">{FORMAT_LABEL[t.source_format] || t.source_format}</Badge>
                          {(t.fields || []).length > 0 && <Badge variant="outline" className="text-[10px]">{t.fields.length} fields</Badge>}
                          {isBuiltin && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border border-indigo-300">Built-in</Badge>}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <Button variant="outline" size="sm" onClick={() => preview(t.id)} data-testid={`preview-${t.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
                      {!isBuiltin && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openTemplate(t.id)} data-testid={`edit-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit Mappings</Button>
                          {['pdf_flat', 'pdf_scanned'].includes(t.source_format) && (
                            <Button variant="outline" size="sm" onClick={() => openPosition(t.id)} data-testid={`position-${t.id}`}><MousePointer2 className="w-3.5 h-3.5 mr-1" /> Position Fields</Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => setAssignFor(t)} data-testid={`assign-${t.id}`}><MapPin className="w-3.5 h-3.5 mr-1" /> Assign</Button>
                          <Button variant="outline" size="sm" onClick={() => toggleStatus(t)} data-testid={`toggle-${t.id}`}><Power className="w-3.5 h-3.5 mr-1" /> {t.status === 'disabled' ? 'Enable' : 'Disable'}</Button>
                          <Button variant="outline" size="sm" onClick={() => duplicate(t.id)} data-testid={`duplicate-${t.id}`}><Copy className="w-3.5 h-3.5 mr-1" /> Duplicate</Button>
                          <Button variant="outline" size="sm" onClick={() => { setReplaceId(t.id); replaceRef.current?.click(); }} data-testid={`replace-${t.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Replace</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => remove(t.id)} data-testid={`delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <input ref={replaceRef} type="file" accept=".pdf,.docx" className="hidden" onChange={onReplaceFile} />

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Application Form</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              PDF, fillable PDF, Word (DOCX) or scanned PDF. We'll read it and suggest how each field maps to your order data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Form file</Label>
              <Input type="file" accept=".pdf,.docx" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
            </div>
            <div>
              <Label className="text-xs">Name (optional)</Label>
              <Input value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="e.g. Newbury Town Council Memorial Application" data-testid="upload-name-input" />
            </div>
            <div>
              <Label className="text-xs">Organisation type (optional)</Label>
              <Select value={uploadOrgType || undefined} onValueChange={setUploadOrgType}>
                <SelectTrigger data-testid="upload-orgtype-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={doUpload} disabled={uploading} data-testid="upload-analyse-btn">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analysing…</> : <><Upload className="w-4 h-4 mr-1.5" /> Upload & Analyse</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplateMappingDialog open={!!mapping} onOpenChange={(o) => !o && setMapping(null)} template={mapping} onSaved={load} />
      <TemplatePositionEditor open={!!positionFor} onOpenChange={(o) => !o && setPositionFor(null)} template={positionFor} onSaved={load} />
      <AssignDialog template={assignFor} onClose={() => setAssignFor(null)} onSaved={load} API={API} auth={auth} />
    </div>
  );
}

function AssignDialog({ template, onClose, onSaved, API, auth }) {
  const [dioceses, setDioceses] = useState('');
  const [councils, setCouncils] = useState('');
  const [orgType, setOrgType] = useState('');
  const [cemeteries, setCemeteries] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!template) return;
    const a = template.assignment || {};
    setDioceses((a.dioceses || []).join(', '));
    setCouncils((a.councils || []).join(', '));
    setOrgType(a.organisation_type || '');
    setCemeteries(a._cemeteries || []);
    setQuery(''); setResults([]);
  }, [template?.id]);

  const search = async (q) => {
    setQuery(q);
    if (!q.trim()) { setResults([]); return; }
    try { const r = await axios.get(`${API}/cemeteries/search`, { ...auth, params: { q } }); setResults(r.data || []); } catch {}
  };

  const save = async () => {
    try {
      await axios.put(`${API}/application-templates/${template.id}`, {
        assignment: {
          cemetery_ids: cemeteries.map((c) => c.id),
          dioceses: dioceses.split(',').map((s) => s.trim()).filter(Boolean),
          councils: councils.split(',').map((s) => s.trim()).filter(Boolean),
          organisation_type: orgType || null,
        },
      }, auth);
      toast.success('Template assignment saved');
      onSaved?.(); onClose();
    } catch { toast.error('Failed to save assignment'); }
  };

  if (!template) return null;
  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign "{template.name}" to authorities</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Orders linked to a matching cemetery, diocese or council will automatically suggest this template. Users can still pick another.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Link specific cemeteries / churches</Label>
            <Input value={query} onChange={(e) => search(e.target.value)} placeholder="Search cemetery / churchyard…" data-testid="assign-cemetery-search" />
            {results.length > 0 && (
              <div className="border rounded-md divide-y max-h-40 overflow-y-auto mt-1">
                {results.map((c) => (
                  <button key={c.id} className="w-full text-left px-3 py-1.5 text-sm hover:bg-teal-50"
                    onClick={() => { if (!cemeteries.find((x) => x.id === c.id)) setCemeteries((cs) => [...cs, c]); setResults([]); setQuery(''); }}>
                    {c.name} <span className="text-xs text-slate-400">{c.postcode}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1 mt-1.5">
              {cemeteries.map((c) => (
                <Badge key={c.id} variant="outline" className="text-xs flex items-center gap-1">
                  {c.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setCemeteries((cs) => cs.filter((x) => x.id !== c.id))} />
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Diocese(s) — comma separated</Label>
            <Input value={dioceses} onChange={(e) => setDioceses(e.target.value)} placeholder="e.g. Oxford" data-testid="assign-dioceses" />
          </div>
          <div>
            <Label className="text-xs">Council(s) — comma separated</Label>
            <Input value={councils} onChange={(e) => setCouncils(e.target.value)} placeholder="e.g. Newbury Town Council" data-testid="assign-councils" />
          </div>
          <div>
            <Label className="text-xs">Organisation type</Label>
            <Select value={orgType || undefined} onValueChange={setOrgType}>
              <SelectTrigger data-testid="assign-orgtype"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>{ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={save} data-testid="assign-save-btn">Save Assignment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
